import React, { useState, useEffect, useCallback } from "react";
import {
  FaUserPlus,
  FaBell,
  FaPhone,
  FaEnvelope,
  FaCalendarAlt,
  FaCheckCircle,
  FaClock,
  FaTimes,
  FaCheck,
  FaSearch,
  FaBriefcase,
  FaCalendarDay,
  FaBan,
  FaTrash,
} from "react-icons/fa";
import axios from "axios";

// Helper to replace fetch with axios under the hood
const axiosRequest = async (url, options = {}) => {
  const { method = 'GET', headers = {}, body } = options;
  try {
    const config = {
      method,
      url,
      headers: {
        'Content-Type': 'application/json',
        ...headers
      },
      withCredentials: true
    };
    if (body) {
      config.data = typeof body === 'string' ? JSON.parse(body) : body;
    }
    const response = await axios(config);
    return {
      ok: true,
      status: response.status,
      json: async () => response.data,
      text: async () => typeof response.data === 'string' ? response.data : JSON.stringify(response.data)
    };
  } catch (error) {
    const status = error.response ? error.response.status : 500;
    const data = error.response ? error.response.data : { error: error.message };
    return {
      ok: false,
      status,
      json: async () => data,
      text: async () => typeof data === 'string' ? data : JSON.stringify(data)
    };
  }
};

// ✅ FIXED: API URL now points to the backend server
const API = "https://apigm.sriganapathymedical.shop/api";

const STATUS_COLORS = {
  Pending: { bg: "#fbbf24", text: "#000" },
  Visited: { bg: "#22c55e", text: "#fff" },
  Cancelled: { bg: "#ef4444", text: "#fff" },
};

// ─────────────────────── helpers ───────────────────────
const todayStr = () => new Date().toISOString().split("T")[0];

const formatDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d + "T00:00:00");
  return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

// ─────────────────────── Notification Banner ───────────────────────
const NotificationPanel = ({ notifications, onDismiss, isMobile }) => {
  if (!notifications.length) return null;
  return (
    <div style={notifStyles.panel(isMobile)}>
      <div style={notifStyles.header}>
        <FaBell style={{ color: "#fbbf24", marginRight: 8 }} />
        <span style={{ fontWeight: 700, color: "#fbbf24" }}>
          {notifications.length} Alert{notifications.length > 1 ? "s" : ""}
        </span>
      </div>
      {notifications.map((n) => (
        <div key={n.id} style={notifStyles.item(isMobile)}>
          <div style={{ flex: 1 }}>
            <span style={notifStyles.name}>{n.customer_name}</span>
            <span style={notifStyles.sub}>
              {n.is_today
                ? n.is_coming_today
                  ? "📍 Coming TODAY — be ready!"
                  : "⚠️ Scheduled today — not confirmed yet"
                : n.is_followup_today
                  ? "📅 Follow-up TODAY!"
                  : `⏰ Overdue since ${formatDate(n.meetup_date)}`}
            </span>
            <span style={notifStyles.phone}>📞 {n.contact_number}</span>
          </div>
          <button style={notifStyles.dismiss(isMobile)} onClick={() => onDismiss(n.id)} title="Dismiss">
            <FaTimes />
          </button>
        </div>
      ))}
    </div>
  );
};

const notifStyles = {
  panel: (isMobile) => ({
    background: "linear-gradient(135deg, #1c1a2e, #2d1b4e)",
    border: "1px solid rgba(251,191,36,0.4)",
    borderRadius: 14,
    padding: isMobile ? "12px 16px" : "16px 20px",
    marginBottom: 24,
    boxShadow: "0 4px 30px rgba(251,191,36,0.15)",
    animation: "pulse 2s ease-in-out infinite",
  }),
  header: {
    display: "flex",
    alignItems: "center",
    marginBottom: 12,
    fontSize: 16,
  },
  item: (isMobile) => ({
    display: "flex",
    flexDirection: isMobile ? "column" : "row",
    alignItems: isMobile ? "flex-start" : "center",
    background: "rgba(255,255,255,0.05)",
    borderRadius: 10,
    padding: isMobile ? "10px 12px" : "10px 14px",
    marginBottom: 8,
    gap: isMobile ? 8 : 12,
    borderLeft: "3px solid #fbbf24",
  }),
  name: { fontWeight: 700, color: "#fff", display: "block", fontSize: 15 },
  sub: { color: "#f9a8d4", fontSize: 13, display: "block", marginTop: 2 },
  phone: { color: "#93c5fd", fontSize: 13, display: "block", marginTop: 2 },
  dismiss: (isMobile) => ({
    background: "rgba(255,255,255,0.1)",
    border: "none",
    borderRadius: 6,
    color: "#9ca3af",
    cursor: "pointer",
    padding: "4px 8px",
    fontSize: 14,
    alignSelf: isMobile ? "flex-end" : "center",
  }),
};

// ─────────────────────── Modal ───────────────────────
const Modal = ({ show, onClose, onSave, initial, isMobile }) => {
  const blank = {
    customer_name: "",
    contact_number: "",
    email: "",
    meetup_date: todayStr(),
    is_coming_today: false,
    medicine_interest: "",
    notes: "",
    status: "Pending",
  };

  const [form, setForm] = useState(blank);

  useEffect(() => {
    if (initial) {
      const { next_followup_date, ...rest } = initial;
      setForm({ ...blank, ...rest, status: "Pending" });
    } else {
      setForm(blank);
    }
  }, [initial, show]);

  if (!show) return null;

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSave = () => {
    if (!form.customer_name.trim()) { alert("Customer Name is required"); return; }
    if (!form.contact_number.trim()) { alert("Contact Number is required"); return; }
    if (!form.meetup_date) { alert("Meet-up Date is required"); return; }
    onSave(form);
  };

  return (
    <div style={modalStyles.overlay}>
      <div style={modalStyles.box(isMobile)}>
        <div style={modalStyles.header}>
          <h3 style={{ margin: 0, color: "#4da6ff", fontSize: isMobile ? 18 : 20 }}>
            {initial ? "✏️ Edit Enquiry" : "➕ New Enquiry"}
          </h3>
          <button style={modalStyles.closeBtn} onClick={onClose}><FaTimes /></button>
        </div>

        <div style={modalStyles.grid(isMobile)}>
          <div style={modalStyles.group}>
            <label style={modalStyles.label}>Customer Name *</label>
            <input style={modalStyles.input} value={form.customer_name}
              onChange={(e) => set("customer_name", e.target.value)} placeholder="Full Name" />
          </div>

          <div style={modalStyles.group}>
            <label style={modalStyles.label}>Contact Number *</label>
            <input style={modalStyles.input} value={form.contact_number}
              onChange={(e) => set("contact_number", e.target.value)} placeholder="10-digit mobile" />
          </div>

          <div style={modalStyles.group}>
            <label style={modalStyles.label}>Email</label>
            <input style={modalStyles.input} type="email" value={form.email}
              onChange={(e) => set("email", e.target.value)} placeholder="customer@email.com" />
          </div>

          <div style={modalStyles.group}>
            <label style={modalStyles.label}>Medicine/Requirement</label>
            <input style={modalStyles.input} value={form.medicine_interest || ""}
              onChange={(e) => set("medicine_interest", e.target.value)} placeholder="e.g. Paracetamol, Insulin, etc." />
          </div>

          <div style={modalStyles.group}>
            <label style={modalStyles.label}>Meet-up Date *</label>
            <input style={modalStyles.input} type="date" value={form.meetup_date}
              onChange={(e) => set("meetup_date", e.target.value)} />
          </div>
        </div>

        <div style={modalStyles.group}>
          <label style={modalStyles.label}>Notes</label>
          <textarea style={{ ...modalStyles.input, resize: "vertical", minHeight: 70 }}
            value={form.notes} onChange={(e) => set("notes", e.target.value)}
            placeholder="Additional notes..." />
        </div>

        <div style={modalStyles.footer(isMobile)}>
          <button style={modalStyles.cancelBtn(isMobile)} onClick={onClose}>Cancel</button>
          <button style={modalStyles.saveBtn(isMobile)} onClick={handleSave}>
            {initial ? "💾 Update" : "➕ Add Enquiry"}
          </button>
        </div>
      </div>
    </div>
  );
};

const modalStyles = {
  overlay: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", display: "flex",
    justifyContent: "center", alignItems: "center", zIndex: 1000, padding: "10px",
  },
  box: (isMobile) => ({
    background: "linear-gradient(135deg, #111827, #1e2a3a)",
    borderRadius: 18, padding: isMobile ? 20 : 28, width: isMobile ? "100%" : "90%",
    maxWidth: 720, maxHeight: "90vh", overflowY: "auto",
    boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
    border: "1px solid rgba(77,166,255,0.2)",
  }),
  header: {
    display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20,
  },
  closeBtn: {
    background: "rgba(255,255,255,0.1)", border: "none", borderRadius: 8,
    color: "#9ca3af", cursor: "pointer", padding: "6px 10px", fontSize: 16,
  },
  grid: (isMobile) => ({
    display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
    gap: isMobile ? "12px 16px" : "14px 20px",
  }),
  group: { display: "flex", flexDirection: "column" },
  label: { fontSize: 13, color: "#9ca3af", marginBottom: 5 },
  input: {
    background: "rgba(255,255,255,0.07)", border: "1px solid rgba(77,166,255,0.3)",
    borderRadius: 8, padding: "9px 12px", color: "#fff", fontSize: 14, outline: "none",
    width: "100%", boxSizing: "border-box",
  },
  footer: (isMobile) => ({
    display: "flex", flexDirection: isMobile ? "column" : "row",
    justifyContent: "flex-end", gap: isMobile ? 8 : 12, marginTop: 20,
  }),
  cancelBtn: (isMobile) => ({
    background: "rgba(255,255,255,0.1)", border: "none", borderRadius: 8,
    color: "#9ca3af", cursor: "pointer", padding: "10px 22px", fontSize: 14,
    width: isMobile ? "100%" : "auto",
  }),
  saveBtn: (isMobile) => ({
    background: "linear-gradient(135deg, #4da6ff, #a855f7)", border: "none", borderRadius: 8,
    color: "#fff", cursor: "pointer", padding: "10px 24px", fontSize: 14, fontWeight: 700,
    width: isMobile ? "100%" : "auto",
  }),
};

// ─────────────────────── Toast ───────────────────────
const Toast = ({ message, show }) => {
  if (!show) return null;
  return (
    <div style={{
      position: "fixed",
      top: 75,
      right: 24,
      zIndex: 9999,
      minWidth: 300,
      maxWidth: "90%",
      background: "linear-gradient(135deg, #14532d, #15803d)",
      border: "1px solid rgba(34,197,94,0.5)",
      color: "#fff",
      borderRadius: 14,
      overflow: "hidden",
      boxShadow: "0 12px 40px rgba(34,197,94,0.35)",
      animation: "toastSlideDown 0.35s cubic-bezier(.22,1,.36,1)",
    }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "14px 18px",
      }}>
        <span style={{
          background: "rgba(255,255,255,0.15)", borderRadius: "50%",
          width: 36, height: 36, display: "flex", alignItems: "center",
          justifyContent: "center", fontSize: 18, flexShrink: 0,
        }}>
          <FaCheckCircle />
        </span>
        <div>
          <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 2 }}>Customer Accepted!</div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.8)" }}>{message}</div>
        </div>
      </div>
      <div style={{
        height: 3,
        background: "rgba(255,255,255,0.15)",
      }}>
        <div style={{
          height: "100%",
          background: "#86efac",
          animation: "toastProgress 3s linear forwards",
        }} />
      </div>
    </div>
  );
};

// ─────────────────────── Main Page ───────────────────────
const EnquiryPage = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [dismissed, setDismissed] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [loading, setLoading] = useState(false);
  const [acceptingId, setAcceptingId] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "" });
  const [updatingCalled, setUpdatingCalled] = useState(null);
  const [showFollowupModal, setShowFollowupModal] = useState(false);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [newFollowupDate, setNewFollowupDate] = useState("");
  const [updatingFollowup, setUpdatingFollowup] = useState(null);

  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Handle window resize
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [eq, nf] = await Promise.all([
        axiosRequest(`${API}/enquiries`).then((r) => r.json()),
        axiosRequest(`${API}/enquiries/notifications`).then((r) => r.json()),
      ]);
      setEnquiries(eq);
      setNotifications(nf);
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSave = async (form) => {
    const method = editTarget ? "PUT" : "POST";
    const url = editTarget ? `${API}/enquiries/${editTarget.id}` : `${API}/enquiries`;
    try {
      const res = await axiosRequest(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, called: false, status: "Pending" }),
      });
      if (!res.ok) { const d = await res.json(); alert(d.error || "Error"); return; }
      setShowModal(false);
      setEditTarget(null);
      fetchAll();
    } catch (err) {
      alert("Server error. Please try again.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    await axiosRequest(`${API}/enquiries/${id}`, { method: "DELETE" });
    fetchAll();
  };

  const handleAccept = async (enq) => {
    if (enq.status === "Visited") return;
    setAcceptingId(enq.id);
    try {
      await axiosRequest(`${API}/enquiries/${enq.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...enq, status: "Visited" }),
      });
      setToast({ show: true, message: `${enq.customer_name} marked as visited!` });
      setTimeout(() => setToast({ show: false, message: "" }), 3000);
      fetchAll();
    } catch (err) {
      alert("Error updating status.");
    } finally {
      setAcceptingId(null);
    }
  };

  const handleCancel = async (enq) => {
    if (enq.status === "Cancelled") return;
    if (!window.confirm(`Cancel enquiry for ${enq.customer_name}?`)) return;
    try {
      await axiosRequest(`${API}/enquiries/${enq.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...enq, status: "Cancelled" }),
      });
      fetchAll();
    } catch (err) {
      alert("Error updating status.");
    }
  };

  const handleToggleCalled = async (enq) => {
    setUpdatingCalled(enq.id);
    try {
      await axiosRequest(`${API}/enquiries/${enq.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...enq, called: !enq.called }),
      });
      fetchAll();
    } catch (err) {
      alert("Error updating called status.");
    } finally {
      setUpdatingCalled(null);
    }
  };

  const handleUpdateFollowup = async () => {
    if (!newFollowupDate) {
      alert("Please select a follow-up date");
      return;
    }
    setUpdatingFollowup(selectedEnquiry.id);
    try {
      await axiosRequest(`${API}/enquiries/${selectedEnquiry.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...selectedEnquiry, next_followup_date: newFollowupDate }),
      });
      setShowFollowupModal(false);
      setSelectedEnquiry(null);
      setNewFollowupDate("");
      fetchAll();
    } catch (err) {
      alert("Error updating follow-up date.");
    } finally {
      setUpdatingFollowup(null);
    }
  };

  const openFollowupModal = (enq) => {
    setSelectedEnquiry(enq);
    setNewFollowupDate(enq.next_followup_date || "");
    setShowFollowupModal(true);
  };

  const handleDismiss = (id) => setDismissed((p) => [...p, id]);

  const visibleNotifs = notifications.filter((n) => !dismissed.includes(n.id));

  const filtered = enquiries.filter((e) => {
    const q = search.toLowerCase();
    const matchSearch = e.customer_name.toLowerCase().includes(q) ||
      e.contact_number.includes(q) || (e.email || "").toLowerCase().includes(q);
    const matchStatus = filterStatus === "All" || e.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const stats = {
    total: enquiries.length,
    pending: enquiries.filter((e) => e.status === "Pending").length,
    visited: enquiries.filter((e) => e.status === "Visited").length,
    today: enquiries.filter((e) => e.is_today).length,
  };

  return (
    <div style={pageStyles.container(isMobile)}>
      <style>{`
        @keyframes pulse { 0%,100%{box-shadow:0 4px 30px rgba(251,191,36,0.15)} 50%{box-shadow:0 4px 40px rgba(251,191,36,0.35)} }
        @keyframes fadeIn { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        @keyframes toastSlideDown { from{opacity:0;transform:translateY(-20px)} to{opacity:1;transform:translateY(0)} }
        @keyframes toastProgress { from{width:100%} to{width:0%} }
        .enquiry-row:hover { background: rgba(77,166,255,0.07) !important; }
        input[type=checkbox] { accent-color: #4da6ff; width:16px; height:16px; cursor:pointer; }
        @media (max-width: 768px) {
          .enquiry-row td { font-size: 12px; padding: 8px 10px; }
          .enquiry-row th { font-size: 11px; padding: 8px 10px; }
        }
        @media (max-width: 480px) {
          .enquiry-row td { font-size: 11px; padding: 6px 8px; }
          .enquiry-row th { font-size: 10px; padding: 6px 8px; }
        }
      `}</style>

      {/* Header */}
      <div style={pageStyles.pageHeader(isMobile)}>
        <div>
          <h2 style={pageStyles.title(isMobile)}>📋 Enquiry Management</h2>
          <p style={pageStyles.subtitle(isMobile)}>Manage customer enquiries and follow-ups</p>
        </div>
        <button style={pageStyles.addBtn(isMobile)} onClick={() => { setEditTarget(null); setShowModal(true); }}>
          <FaUserPlus style={{ marginRight: 8 }} /> {isMobile ? "New" : "New Enquiry"}
        </button>
      </div>

      {/* Stats */}
      <div style={pageStyles.statsGrid(isMobile)}>
        {[
          { label: "Total", value: stats.total, color: "#4da6ff", icon: "📋" },
          { label: "Pending", value: stats.pending, color: "#fbbf24", icon: "⏳" },
          { label: "Visited", value: stats.visited, color: "#22c55e", icon: "✅" },
          { label: "Today", value: stats.today, color: "#a855f7", icon: "📅" },
        ].map((s) => (
          <div key={s.label} style={{ ...pageStyles.statCard(isMobile), borderTop: `3px solid ${s.color}` }}>
            <div style={{ fontSize: isMobile ? 22 : 28 }}>{s.icon}</div>
            <div style={{ color: s.color, fontSize: isMobile ? 22 : 28, fontWeight: 800 }}>{s.value}</div>
            <div style={{ color: "#6b7280", fontSize: isMobile ? 11 : 13 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Notifications */}
      <NotificationPanel notifications={visibleNotifs} onDismiss={handleDismiss} isMobile={isMobile} />

      {/* Filters */}
      <div style={pageStyles.toolbar(isMobile)}>
        <div style={pageStyles.searchBox(isMobile)}>
          <FaSearch style={{ color: "#6b7280", marginRight: 8 }} />
          <input style={pageStyles.searchInput} placeholder={isMobile ? "Search..." : "Search name, phone, email..."}
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div style={pageStyles.filterGroup(isMobile)}>
          {["All", "Pending", "Visited", "Cancelled"].map((s) => (
            <button key={s} style={{
              ...pageStyles.filterBtn(isMobile),
              ...(filterStatus === s ? pageStyles.filterBtnActive : {}),
            }} onClick={() => setFilterStatus(s)}>{isMobile && s !== "All" ? s.substring(0, 3) : s}</button>
          ))}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 60, color: "#6b7280" }}>Loading enquiries...</div>
      ) : filtered.length === 0 ? (
        <div style={pageStyles.emptyState}>
          <div style={{ fontSize: isMobile ? 40 : 56 }}>📭</div>
          <div style={{ color: "#6b7280", marginTop: 12 }}>No enquiries found</div>
          <button style={pageStyles.addBtn(isMobile)} onClick={() => { setEditTarget(null); setShowModal(true); }}>
            Create your first enquiry
          </button>
        </div>
      ) : (
        <div style={pageStyles.tableWrapper}>
          <table style={pageStyles.table}>
            <thead>
              <tr>
                {isMobile ? (
                  <>
                    <th style={pageStyles.th(isMobile)}>Patient</th>
                    <th style={pageStyles.th(isMobile)}>Contact</th>
                    <th style={pageStyles.th(isMobile)}>Status</th>
                    <th style={pageStyles.th(isMobile)}>Actions</th>
                  </>
                ) : (
                  <>
                    <th style={pageStyles.th(isMobile)}>Patient/Customer</th>
                    <th style={pageStyles.th(isMobile)}>Contact</th>
                    <th style={pageStyles.th(isMobile)}>Email</th>
                    <th style={pageStyles.th(isMobile)}>Requirement</th>
                    <th style={pageStyles.th(isMobile)}>Meet-up Date</th>
                    <th style={pageStyles.th(isMobile)}>Coming Today?</th>
                    <th style={pageStyles.th(isMobile)}>Status</th>
                    <th style={pageStyles.th(isMobile)}>Called</th>
                    <th style={pageStyles.th(isMobile)}>Next Follow-up</th>
                    <th style={pageStyles.th(isMobile)}>Actions</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => {
                const rowAlert = e.is_today || e.is_overdue;
                return (
                  <tr key={e.id} className="enquiry-row" style={{
                    ...pageStyles.tr,
                    borderLeft: rowAlert ? "3px solid #fbbf24" : "3px solid transparent",
                  }}>
                    {isMobile ? (
                      // Mobile view - compact with Delete button
                      <>
                        <td style={pageStyles.td(isMobile)}>
                          <div style={{ fontWeight: 700, color: "#fff" }}>{e.customer_name}</div>
                          {e.is_today && <span style={pageStyles.todayBadge}>TODAY</span>}
                          {e.is_overdue && !e.is_today && <span style={pageStyles.overdueBadge}>OVERDUE</span>}
                          <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                            {e.medicine_interest || "No requirement"}
                          </div>
                        </td>
                        <td style={pageStyles.td(isMobile)}>
                          <FaPhone style={{ color: "#4da6ff", marginRight: 5 }} />{e.contact_number}
                          {e.next_followup_date && (
                            <div style={{ fontSize: 11, color: "#fbbf24", marginTop: 4 }}>
                              <FaCalendarDay style={{ marginRight: 4 }} />
                              {formatDate(e.next_followup_date)}
                            </div>
                          )}
                        </td>
                        <td style={pageStyles.td(isMobile)}>
                          {e.status === "Visited" ? (
                            <span style={pageStyles.visitedChip}>✅ Visited</span>
                          ) : e.status === "Cancelled" ? (
                            <span style={{ ...pageStyles.badge, background: "#ef4444", color: "#fff", padding: "2px 8px", borderRadius: 4, fontSize: 11 }}>
                              Cancelled
                            </span>
                          ) : (
                            <button
                              style={pageStyles.visitBtn(isMobile)}
                              onClick={() => handleAccept(e)}
                              disabled={acceptingId === e.id}
                            >
                              {acceptingId === e.id ? "..." : "Mark Visited"}
                            </button>
                          )}
                        </td>
                        <td style={pageStyles.td(isMobile)}>
                          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                            <button
                              style={pageStyles.calledBtn(isMobile)}
                              onClick={() => handleToggleCalled(e)}
                              disabled={updatingCalled === e.id}
                            >
                              {updatingCalled === e.id ? "..." : <FaCheck />}
                              <span style={{ marginLeft: 4 }}>{e.called ? "✓" : "○"}</span>
                            </button>
                            <button
                              style={pageStyles.followupBtn(isMobile)}
                              onClick={() => openFollowupModal(e)}
                            >
                              <FaCalendarDay />
                            </button>
                            {e.status !== "Cancelled" && e.status !== "Visited" && (
                              <button
                                style={pageStyles.cancelBtn(isMobile)}
                                onClick={() => handleCancel(e)}
                              >
                                <FaBan />
                              </button>
                            )}
                            <button
                              style={pageStyles.deleteBtn(isMobile)}
                              onClick={() => handleDelete(e.id)}
                              title="Delete"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      // Desktop view - full with Delete button
                      <>
                        <td style={pageStyles.td(isMobile)}>
                          <div style={{ fontWeight: 700, color: "#fff" }}>{e.customer_name}</div>
                          {e.is_today && <span style={pageStyles.todayBadge}>TODAY</span>}
                          {e.is_overdue && !e.is_today && <span style={pageStyles.overdueBadge}>OVERDUE</span>}
                        </td>
                        <td style={pageStyles.td(isMobile)}><FaPhone style={{ color: "#4da6ff", marginRight: 5 }} />{e.contact_number}</td>
                        <td style={pageStyles.td(isMobile)}>{e.email ? <><FaEnvelope style={{ color: "#a855f7", marginRight: 5 }} />{e.email}</> : "—"}</td>
                        <td style={pageStyles.td(isMobile)}>{e.medicine_interest ? <><FaBriefcase style={{ color: "#22c55e", marginRight: 5 }} />{e.medicine_interest}</> : "—"}</td>
                        <td style={pageStyles.td(isMobile)}>
                          <FaCalendarAlt style={{ color: "#fbbf24", marginRight: 5 }} />
                          {formatDate(e.meetup_date)}
                        </td>
                        <td style={{ ...pageStyles.td(isMobile), textAlign: "center" }}>
                          {e.is_coming_today
                            ? <FaCheckCircle style={{ color: "#22c55e", fontSize: 18 }} />
                            : <FaClock style={{ color: "#9ca3af", fontSize: 18 }} />}
                        </td>
                        <td style={pageStyles.td(isMobile)}>
                          {e.status === "Visited" ? (
                            <span style={pageStyles.visitedChip}>✅ Visited</span>
                          ) : e.status === "Cancelled" ? (
                            <span style={{ ...pageStyles.badge, background: "#ef4444", color: "#fff" }}>Cancelled</span>
                          ) : (
                            <button
                              style={pageStyles.visitBtn(isMobile)}
                              onClick={() => handleAccept(e)}
                              disabled={acceptingId === e.id}
                            >
                              {acceptingId === e.id ? "..." : "Mark Visited"}
                            </button>
                          )}
                        </td>
                        <td style={{ ...pageStyles.td(isMobile), textAlign: "center" }}>
                          <button
                            style={pageStyles.calledBtn(isMobile)}
                            onClick={() => handleToggleCalled(e)}
                            disabled={updatingCalled === e.id}
                          >
                            {updatingCalled === e.id ? "..." : <FaCheck />}
                            <span style={{ marginLeft: 6 }}>{e.called ? "Called" : "Not Called"}</span>
                          </button>
                        </td>
                        <td style={pageStyles.td(isMobile)}>
                          {e.next_followup_date ? (
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <FaCalendarDay style={{ color: "#fbbf24" }} />
                              <span>{formatDate(e.next_followup_date)}</span>
                            </div>
                          ) : "—"}
                        </td>
                        <td style={pageStyles.td(isMobile)}>
                          <div style={{ display: "flex", gap: 8 }}>
                            <button
                              style={pageStyles.followupBtn(isMobile)}
                              onClick={() => openFollowupModal(e)}
                            >
                              <FaCalendarDay />
                            </button>
                            {e.status !== "Cancelled" && e.status !== "Visited" && (
                              <button
                                style={pageStyles.cancelBtn(isMobile)}
                                onClick={() => handleCancel(e)}
                              >
                                <FaBan />
                              </button>
                            )}
                            <button
                              style={pageStyles.deleteBtn(isMobile)}
                              onClick={() => handleDelete(e.id)}
                              title="Delete"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal show={showModal} onClose={() => { setShowModal(false); setEditTarget(null); }}
        onSave={handleSave} initial={editTarget} isMobile={isMobile} />

      {/* Follow-up Modal */}
      {showFollowupModal && selectedEnquiry && (
        <div style={modalStyles.overlay}>
          <div style={modalStyles.box(isMobile)}>
            <div style={modalStyles.header}>
              <h3 style={{ margin: 0, color: "#4da6ff", fontSize: isMobile ? 18 : 20 }}>
                📅 Set Next Follow-up Date
              </h3>
              <button style={modalStyles.closeBtn} onClick={() => setShowFollowupModal(false)}>
                <FaTimes />
              </button>
            </div>
            <div style={{ padding: "20px 0" }}>
              <div style={modalStyles.group}>
                <label style={modalStyles.label}>Customer: {selectedEnquiry.customer_name}</label>
                <input
                  style={modalStyles.input}
                  type="date"
                  value={newFollowupDate}
                  onChange={(e) => setNewFollowupDate(e.target.value)}
                  min={todayStr()}
                />
              </div>
            </div>
            <div style={modalStyles.footer(isMobile)}>
              <button style={modalStyles.cancelBtn(isMobile)} onClick={() => setShowFollowupModal(false)}>
                Cancel
              </button>
              <button
                style={modalStyles.saveBtn(isMobile)}
                onClick={handleUpdateFollowup}
                disabled={updatingFollowup === selectedEnquiry.id}
              >
                {updatingFollowup === selectedEnquiry.id ? "Saving..." : "Save Follow-up Date"}
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast show={toast.show} message={toast.message} />
    </div>
  );
};

// ─────────────────────── Styles ───────────────────────
const pageStyles = {
  container: (isMobile) => ({
    padding: isMobile ? "10px" : "20px",
    animation: "fadeIn 0.4s ease",
    color: "#fff",
    minHeight: "100vh",
    backgroundColor: "#000",
    borderRadius: isMobile ? "8px" : "16px",
  }),
  pageHeader: (isMobile) => ({
    display: "flex",
    flexDirection: isMobile ? "column" : "row",
    justifyContent: "space-between",
    alignItems: isMobile ? "stretch" : "flex-start",
    marginBottom: 28,
    flexWrap: "wrap",
    gap: isMobile ? 12 : 12,
  }),
  title: (isMobile) => ({
    margin: 0,
    fontSize: isMobile ? 20 : 28,
    fontWeight: 800,
    color: "#fff",
    letterSpacing: "-0.5px",
  }),
  subtitle: (isMobile) => ({
    margin: "4px 0 0",
    color: "#94a3b8",
    fontSize: isMobile ? 13 : 14,
  }),
  addBtn: (isMobile) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #4da6ff, #a855f7)",
    border: "none",
    borderRadius: 10,
    color: "#fff",
    cursor: "pointer",
    padding: isMobile ? "10px 16px" : "11px 22px",
    fontWeight: 700,
    fontSize: isMobile ? 14 : 14,
    boxShadow: "0 4px 15px rgba(77,166,255,0.3)",
    transition: "transform 0.2s, box-shadow 0.2s",
    width: isMobile ? "100%" : "auto",
  }),
  statsGrid: (isMobile) => ({
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)",
    gap: isMobile ? 10 : 16,
    marginBottom: 24,
  }),
  statCard: (isMobile) => ({
    background: "rgba(255,255,255,0.03)",
    borderRadius: 14,
    padding: isMobile ? "14px 12px" : "18px 20px",
    display: "flex",
    flexDirection: "column",
    gap: 4,
    textAlign: "center",
    backdropFilter: "blur(10px)",
    border: "1px solid rgba(255,255,255,0.1)",
  }),
  toolbar: (isMobile) => ({
    display: "flex",
    flexDirection: isMobile ? "column" : "row",
    gap: isMobile ? 10 : 12,
    marginBottom: 20,
    flexWrap: "wrap",
    alignItems: "center",
  }),
  searchBox: (isMobile) => ({
    display: "flex",
    alignItems: "center",
    background: "rgba(255,255,255,0.07)",
    borderRadius: 10,
    padding: "9px 14px",
    flex: 1,
    minWidth: isMobile ? "100%" : 200,
    border: "1px solid rgba(77,166,255,0.2)",
  }),
  searchInput: {
    background: "transparent",
    border: "none",
    color: "#fff",
    outline: "none",
    flex: 1,
    fontSize: 14,
  },
  filterGroup: (isMobile) => ({
    display: "flex",
    gap: isMobile ? 4 : 6,
    flexWrap: "wrap",
    width: isMobile ? "100%" : "auto",
  }),
  filterBtn: (isMobile) => ({
    background: "rgba(255,255,255,0.07)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 8,
    color: "#9ca3af",
    cursor: "pointer",
    padding: isMobile ? "6px 12px" : "8px 16px",
    fontSize: isMobile ? 12 : 13,
    transition: "all 0.2s",
    flex: isMobile ? "1" : "auto",
  }),
  filterBtnActive: {
    background: "rgba(77,166,255,0.2)",
    color: "#4da6ff",
    borderColor: "#4da6ff",
  },
  tableWrapper: {
    overflowX: "auto",
    borderRadius: 16,
    border: "1px solid rgba(255,255,255,0.1)",
    background: "rgba(255,255,255,0.02)",
    margin: "0 -4px",
    padding: "0 4px",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 14,
    minWidth: 600,
  },
  th: (isMobile) => ({
    background: "rgba(255,255,255,0.08)",
    color: "#94a3b8",
    fontWeight: 600,
    padding: isMobile ? "8px 10px" : "13px 14px",
    textAlign: "left",
    whiteSpace: "nowrap",
    borderBottom: "1px solid rgba(255,255,255,0.1)",
    fontSize: isMobile ? 10 : 13,
  }),
  tr: {
    borderBottom: "1px solid rgba(255,255,255,0.05)",
    transition: "background 0.2s",
  },
  td: (isMobile) => ({
    padding: isMobile ? "8px 10px" : "12px 14px",
    color: "#d1d5db",
    verticalAlign: "middle",
    fontSize: isMobile ? 12 : 14,
  }),
  badge: {
    borderRadius: 20,
    padding: "3px 12px",
    fontSize: 12,
    fontWeight: 700,
  },
  todayBadge: {
    background: "rgba(168,85,247,0.2)",
    color: "#a855f7",
    borderRadius: 6,
    fontSize: 10,
    fontWeight: 700,
    padding: "2px 7px",
    marginLeft: 6,
    letterSpacing: 0.5,
  },
  overdueBadge: {
    background: "rgba(239,68,68,0.2)",
    color: "#ef4444",
    borderRadius: 6,
    fontSize: 10,
    fontWeight: 700,
    padding: "2px 7px",
    marginLeft: 6,
    letterSpacing: 0.5,
  },
  visitBtn: (isMobile) => ({
    background: "linear-gradient(135deg, #22c55e, #16a34a)",
    border: "none",
    borderRadius: 7,
    color: "#fff",
    cursor: "pointer",
    padding: isMobile ? "4px 10px" : "6px 14px",
    fontSize: isMobile ? 10 : 12,
    fontWeight: 600,
    boxShadow: "0 2px 8px rgba(34,197,94,0.3)",
    transition: "transform 0.15s, box-shadow 0.15s",
    whiteSpace: "nowrap",
    width: isMobile ? "100%" : "auto",
  }),
  cancelBtn: (isMobile) => ({
    background: "linear-gradient(135deg, #ef4444, #dc2626)",
    border: "none",
    borderRadius: 7,
    color: "#fff",
    cursor: "pointer",
    padding: isMobile ? "4px 8px" : "7px 11px",
    fontSize: isMobile ? 12 : 15,
    fontWeight: 700,
    boxShadow: "0 2px 10px rgba(239,68,68,0.35)",
    transition: "transform 0.15s, box-shadow 0.15s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }),
  followupBtn: (isMobile) => ({
    background: "linear-gradient(135deg, #fbbf24, #f59e0b)",
    border: "none",
    borderRadius: 7,
    color: "#000",
    cursor: "pointer",
    padding: isMobile ? "4px 8px" : "7px 11px",
    fontSize: isMobile ? 12 : 15,
    fontWeight: 700,
    boxShadow: "0 2px 10px rgba(251,191,36,0.35)",
    transition: "transform 0.15s, box-shadow 0.15s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }),
  // ✅ NEW: Delete button style
  deleteBtn: (isMobile) => ({
    background: "rgba(255,255,255,0.1)",
    border: "none",
    borderRadius: 7,
    color: "#f87171",
    cursor: "pointer",
    padding: isMobile ? "4px 8px" : "7px 11px",
    fontSize: isMobile ? 12 : 15,
    fontWeight: 700,
    transition: "transform 0.15s, box-shadow 0.15s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }),
  calledBtn: (isMobile) => ({
    border: "none",
    borderRadius: 7,
    cursor: "pointer",
    padding: isMobile ? "4px 8px" : "7px 12px",
    fontSize: isMobile ? 10 : 12,
    fontWeight: 600,
    transition: "all 0.15s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
    background: "rgba(255,255,255,0.1)",
    color: "#9ca3af",
  }),
  visitedChip: {
    background: "rgba(34,197,94,0.15)",
    color: "#22c55e",
    borderRadius: 7,
    padding: "5px 12px",
    fontSize: 12,
    fontWeight: 700,
    whiteSpace: "nowrap",
    display: "inline-block",
  },
  emptyState: {
    textAlign: "center",
    padding: "60px 20px",
  },
};

export default EnquiryPage;